import React, { useEffect, useRef, useState } from "react";
import {
  Card, Button, Table, Alert, Modal, Form, Badge, Row, Col, Spinner, InputGroup
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus, faTrash, faArrowUp, faArrowDown, faImage, faVideo, faFileAudio
} from "@fortawesome/free-solid-svg-icons";
import api from "../../utils/api";
import { uploadToR2, generateUUID } from "../../utils/r2Upload";

export default () => {
  const [members, setMembers] = useState([]);
  const [fakeAccounts, setFakeAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [selectedFakeIds, setSelectedFakeIds] = useState([]);
  const [addingPool, setAddingPool] = useState(false);

  const [activeMember, setActiveMember] = useState(null);
  const [steps, setSteps] = useState([]);
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepText, setStepText] = useState("");
  const [stepMediaType, setStepMediaType] = useState("text");
  const [stepFile, setStepFile] = useState(null);
  const [savingStep, setSavingStep] = useState(false);

  const dragItem = useRef();
  const dragOverItem = useRef();

  const loadPool = async () => {
    try {
      setLoading(true);
      const res = await api.get("/auto-msg/pool");
      setMembers(res.data?.members || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFakes = async () => {
    try {
      const res = await api.get("/users", { params: { isTestUser: true, limit: 1000 } });
      const users = res.data?.users || [];
      setFakeAccounts(
        users.map((u) => ({
          id: u.uid || u._id,
          name: u.displayName || u.name || u.firstName || "İsimsiz",
          profileImageUrl: u.profileImageUrl
        })).sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadPool();
    loadFakes();
  }, []);

  const openMember = async (member) => {
    setActiveMember(member);
    setSteps(member.steps || []);
  };

  const toggleFakeSelect = (id) => {
    setSelectedFakeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openAddModal = () => {
    setAccountSearch("");
    setSelectedFakeIds([]);
    setShowAddModal(true);
  };

  const selectAllFiltered = () => {
    setSelectedFakeIds(filteredFakes.map((a) => a.id));
  };

  const clearSelection = () => setSelectedFakeIds([]);

  const addToPool = async () => {
    if (!selectedFakeIds.length) return;
    try {
      setAddingPool(true);
      const results = await Promise.allSettled(
        selectedFakeIds.map((fakeUid) => api.post("/auto-msg/pool", { fakeUid }))
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      setShowAddModal(false);
      setSelectedFakeIds([]);
      if (fail === 0) {
        setSuccess(`${ok} hesap havuza eklendi.`);
      } else {
        setSuccess(`${ok} eklendi, ${fail} eklenemedi.`);
        const firstErr = results.find((r) => r.status === "rejected");
        if (firstErr) {
          setError(firstErr.reason?.response?.data?.error || firstErr.reason?.message || "Bazı hesaplar eklenemedi.");
        }
      }
      await loadPool();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setAddingPool(false);
    }
  };

  const removeFromPool = async (fakeUid) => {
    if (!window.confirm("Bu fake hesabı havuzdan çıkarmak istiyor musunuz?")) return;
    try {
      await api.delete(`/auto-msg/pool/${fakeUid}`);
      if (activeMember?.fakeUid === fakeUid) {
        setActiveMember(null);
        setSteps([]);
      }
      await loadPool();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const openNewStep = () => {
    setEditingStep(null);
    setStepText("");
    setStepMediaType("text");
    setStepFile(null);
    setShowStepModal(true);
  };

  const openEditStep = (step) => {
    setEditingStep(step);
    setStepText(step.text || "");
    setStepMediaType(step.mediaType || "text");
    setStepFile(null);
    setShowStepModal(true);
  };

  const saveStep = async () => {
    if (!activeMember) return;
    try {
      setSavingStep(true);
      let mediaUrl = editingStep?.mediaUrl || null;
      let thumbnailUrl = editingStep?.thumbnailUrl || null;
      let mediaType = stepMediaType;

      if (stepFile) {
        const path = `auto-msg/${activeMember.fakeUid}/${generateUUID()}_${stepFile.name}`;
        mediaUrl = await uploadToR2(stepFile, path);
        if (stepFile.type.startsWith("image/")) mediaType = "image";
        else if (stepFile.type.startsWith("video/")) mediaType = "video";
        else if (stepFile.type.startsWith("audio/")) mediaType = "audio";
      }

      if (!stepText.trim() && !mediaUrl) {
        setError("Metin veya medya gerekli.");
        return;
      }

      if (editingStep) {
        const res = await api.put(
          `/auto-msg/pool/${activeMember.fakeUid}/steps/${editingStep.id}`,
          { text: stepText, mediaType, mediaUrl, thumbnailUrl }
        );
        setSteps(res.data.steps || []);
      } else {
        const res = await api.post(`/auto-msg/pool/${activeMember.fakeUid}/steps`, {
          text: stepText,
          mediaType,
          mediaUrl,
          thumbnailUrl
        });
        setSteps(res.data.steps || []);
      }
      setShowStepModal(false);
      await loadPool();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSavingStep(false);
    }
  };

  const deleteStep = async (stepId) => {
    if (!window.confirm("Bu mesaj adımını silmek istiyor musunuz?")) return;
    try {
      const res = await api.delete(`/auto-msg/pool/${activeMember.fakeUid}/steps/${stepId}`);
      setSteps(res.data.steps || []);
      await loadPool();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const moveStep = async (index, direction) => {
    const next = [...steps];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    setSteps(next);
    try {
      const res = await api.put(`/auto-msg/pool/${activeMember.fakeUid}/steps-reorder`, {
        stepIds: next.map((s) => s.id)
      });
      setSteps(res.data.steps || next);
      await loadPool();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      openMember(activeMember);
    }
  };

  const handleDragEnd = async () => {
    if (dragItem.current == null || dragOverItem.current == null) return;
    if (dragItem.current === dragOverItem.current) return;
    const next = [...steps];
    const [item] = next.splice(dragItem.current, 1);
    next.splice(dragOverItem.current, 0, item);
    dragItem.current = null;
    dragOverItem.current = null;
    setSteps(next);
    try {
      const res = await api.put(`/auto-msg/pool/${activeMember.fakeUid}/steps-reorder`, {
        stepIds: next.map((s) => s.id)
      });
      setSteps(res.data.steps || next);
      await loadPool();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const filteredFakes = fakeAccounts.filter((a) => {
    if (members.some((m) => m.fakeUid === a.id)) return false;
    if (!accountSearch.trim()) return true;
    return a.name.toLowerCase().includes(accountSearch.toLowerCase());
  });

  if (loading) {
    return <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>;
  }

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Fake Hesap Havuzu</h4>
          <p className="text-muted mb-0">Havuza fake ekleyin; her birine sıralı mesaj dizisi tanımlayın.</p>
        </div>
        <Button variant="primary" onClick={openAddModal}>
          <FontAwesomeIcon icon={faPlus} className="me-2" /> Fake Ekle
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <Row>
        <Col lg={5}>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Body className="p-0">
              <Table responsive hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Fake</th>
                    <th>Mesaj</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 && (
                    <tr><td colSpan={3} className="text-center text-muted py-4">Havuz boş</td></tr>
                  )}
                  {members.map((m) => {
                    const name = m.user?.displayName || m.user?.name || m.user?.firstName || m.fakeUid;
                    return (
                      <tr
                        key={m.fakeUid}
                        style={{ cursor: "pointer", background: activeMember?.fakeUid === m.fakeUid ? "#f1f3f9" : undefined }}
                        onClick={() => openMember(m)}
                      >
                        <td>
                          <div className="d-flex align-items-center">
                            {m.user?.profileImageUrl && (
                              <img src={m.user.profileImageUrl} alt="" width={36} height={36} className="rounded-circle me-2 object-fit-cover" />
                            )}
                            <div>
                              <div className="fw-bold">{name}</div>
                              <small className="text-muted">{m.enabled === false ? "Pasif" : "Aktif"}</small>
                            </div>
                          </div>
                        </td>
                        <td><Badge bg="primary">{m.stepCount || 0}</Badge></td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-danger" onClick={(e) => { e.stopPropagation(); removeFromPool(m.fakeUid); }}>
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card border="light" className="shadow-sm">
            <Card.Body>
              {!activeMember ? (
                <p className="text-muted mb-0">Mesaj dizisini düzenlemek için soldan bir fake seçin.</p>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      Mesaj Dizisi — {activeMember.user?.displayName || activeMember.user?.firstName || activeMember.fakeUid}
                    </h5>
                    <Button size="sm" variant="primary" onClick={openNewStep}>
                      <FontAwesomeIcon icon={faPlus} className="me-1" /> Mesaj Ekle
                    </Button>
                  </div>

                  <Table responsive hover className="align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>İçerik</th>
                        <th>Tip</th>
                        <th style={{ width: 140 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {steps.length === 0 && (
                        <tr><td colSpan={4} className="text-center text-muted py-3">Henüz mesaj yok</td></tr>
                      )}
                      {steps.map((s, index) => (
                        <tr
                          key={s.id}
                          draggable
                          onDragStart={() => { dragItem.current = index; }}
                          onDragEnter={() => { dragOverItem.current = index; }}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => e.preventDefault()}
                          style={{ cursor: "grab" }}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: 280 }}>
                              {s.text || <span className="text-muted">(sadece medya)</span>}
                            </div>
                            {s.mediaUrl && (
                              <small className="text-muted d-block text-truncate" style={{ maxWidth: 280 }}>{s.mediaUrl}</small>
                            )}
                          </td>
                          <td><Badge bg="secondary">{s.mediaType || "text"}</Badge></td>
                          <td className="text-end">
                            <Button size="sm" variant="light" className="me-1" onClick={() => moveStep(index, -1)}>
                              <FontAwesomeIcon icon={faArrowUp} />
                            </Button>
                            <Button size="sm" variant="light" className="me-1" onClick={() => moveStep(index, 1)}>
                              <FontAwesomeIcon icon={faArrowDown} />
                            </Button>
                            <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEditStep(s)}>Düzenle</Button>
                            <Button size="sm" variant="outline-danger" onClick={() => deleteStep(s.id)}>
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Form.Text className="text-muted">Sürükle-bırak veya ok tuşlarıyla sıralayabilirsiniz.</Form.Text>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showAddModal} onHide={() => { setShowAddModal(false); setSelectedFakeIds([]); }} size="lg">
        <Modal.Header closeButton><Modal.Title>Havuza Fake Ekle</Modal.Title></Modal.Header>
        <Modal.Body>
          <InputGroup className="mb-2">
            <Form.Control
              placeholder="İsim ara..."
              value={accountSearch}
              onChange={(e) => setAccountSearch(e.target.value)}
            />
          </InputGroup>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">
              {selectedFakeIds.length > 0
                ? `${selectedFakeIds.length} hesap seçili`
                : "Birden fazla hesap seçebilirsiniz"}
            </small>
            <div>
              <Button size="sm" variant="link" className="p-0 me-3" onClick={selectAllFiltered} disabled={!filteredFakes.length}>
                Tümünü seç
              </Button>
              <Button size="sm" variant="link" className="p-0" onClick={clearSelection} disabled={!selectedFakeIds.length}>
                Temizle
              </Button>
            </div>
          </div>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {filteredFakes.length === 0 && (
              <div className="text-center text-muted py-4">Eklenecek hesap kalmadı</div>
            )}
            {filteredFakes.map((a) => {
              const checked = selectedFakeIds.includes(a.id);
              return (
                <div
                  key={a.id}
                  className={`d-flex align-items-center p-2 rounded mb-1 ${checked ? "bg-primary text-white" : "bg-light"}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleFakeSelect(a.id)}
                >
                  <Form.Check
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleFakeSelect(a.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="me-2"
                  />
                  {a.profileImageUrl && (
                    <img src={a.profileImageUrl} alt="" width={32} height={32} className="rounded-circle me-2" />
                  )}
                  <span>{a.name}</span>
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowAddModal(false); setSelectedFakeIds([]); }}>İptal</Button>
          <Button variant="primary" disabled={!selectedFakeIds.length || addingPool} onClick={addToPool}>
            {addingPool
              ? "Ekleniyor..."
              : selectedFakeIds.length > 1
                ? `${selectedFakeIds.length} Hesabı Ekle`
                : "Ekle"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showStepModal} onHide={() => setShowStepModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingStep ? "Mesajı Düzenle" : "Mesaj Ekle"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Mesaj metni</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={stepText}
              onChange={(e) => setStepText(e.target.value)}
              placeholder="Örn: Merhaba {{first_uye_name}}, {{uye_sehir_da}} misin?"
            />
            <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
              <small className="text-muted me-1">Değişken ekle:</small>
              {[
                ["{{first_uye_name}}", "İsim"],
                ["{{uye_sehir}}", "Şehir"],
                ["{{uye_sehir_da}}", "Şehir'de"]
              ].map(([token, label]) => (
                <Button
                  key={token}
                  size="sm"
                  variant="outline-secondary"
                  type="button"
                  onClick={() => setStepText((prev) => `${prev || ""}${token}`)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Medya tipi</Form.Label>
            <Form.Select value={stepMediaType} onChange={(e) => setStepMediaType(e.target.value)}>
              <option value="text">Sadece yazı</option>
              <option value="image">Fotoğraf (+ yazı opsiyonel)</option>
              <option value="video">Video (+ yazı opsiyonel)</option>
              <option value="audio">Ses (+ yazı opsiyonel)</option>
            </Form.Select>
          </Form.Group>
          {stepMediaType !== "text" && (
            <Form.Group>
              <Form.Label>
                <FontAwesomeIcon icon={stepMediaType === "video" ? faVideo : stepMediaType === "audio" ? faFileAudio : faImage} className="me-2" />
                Dosya
              </Form.Label>
              <Form.Control
                type="file"
                accept={stepMediaType === "image" ? "image/*" : stepMediaType === "video" ? "video/*" : "audio/*"}
                onChange={(e) => setStepFile(e.target.files?.[0] || null)}
              />
              {editingStep?.mediaUrl && !stepFile && (
                <Form.Text className="text-muted d-block text-truncate">Mevcut: {editingStep.mediaUrl}</Form.Text>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStepModal(false)}>İptal</Button>
          <Button variant="primary" disabled={savingStep} onClick={saveStep}>
            {savingStep ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
